import { Switch, Route, useLocation } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import posthog from '@/lib/posthog';
import '@/lib/facebookPixel';
import { HelmetProvider } from 'react-helmet-async';
import React, { Suspense, lazy, useEffect } from 'react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { VideoPortalProvider } from '@/contexts/VideoPortalContext';
import GlobalVideoPortal from '@/components/GlobalVideoPortal';

// Lazily load page components
const NotFound = lazy(() => import("@/pages/not-found"));
const HomePage = lazy(() => import("@/pages"));
const ProductPage = lazy(() => import("@/pages/ProductPage"));
const BlogFeedPage = lazy(() => import("@/pages/resources/blogs/BlogFeedPage"));
const BlogPostPage = lazy(() => import("@/pages/resources/blogs/BlogPostPage"));
const WaitlistPage = lazy(() => import("@/pages/WaitlistPage"));
const DeliveryTermsPage = lazy(() => import("@/pages/legal/DeliveryTermsPage"));
const ReturnPolicyPage = lazy(() => import("@/pages/legal/ReturnPolicyPage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
const PrivacyPolicyPage = lazy(() => import("@/pages/legal/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("@/pages/legal/TermsOfServicePage"));
const MeditationsPage = lazy(() => import("@/pages/MeditationsPage"));
const TestPage = lazy(() => import('./pages/test'));
const FamilyStoriesPage = lazy(() => import('@/pages/family-stories'));
const PhoneBackgroundsPage = lazy(() => import('@/pages/PhoneBackgroundsPage'));
const FirstCrossingWatchPage = lazy(() => import('@/pages/watch/FirstCrossingWatchPage'));
const FourOrdersPage = lazy(() => import('@/pages/watch/FourOrdersPage'));
const MainBranchHomePage = lazy(() => import('@/pages/MainBranchHomePage'));
const LivingPlushieProductPage = lazy(() => import('@/pages/products/plushie'));
const MerchPage = lazy(() => import('@/pages/MerchPage'));
const MerchProductPage = lazy(() => import('@/pages/MerchProductPage'));
const OpenAppRedirect = lazy(() => import('@/pages/OpenAppRedirect'));
const AppBridgePage = lazy(() => import('@/pages/AppBridgePage')); // TEMP_APP_LINK_BRIDGE
const ReserveConfirmationPage = lazy(() => import('@/pages/ReserveConfirmationPage'));
const ReserveSubscriptionConfirmedPage = lazy(() => import('@/pages/ReserveSubscriptionConfirmedPage'));
const LivingPlushieInstructionsPage = lazy(() => import('@/pages/products/LivingPlushieInstructionsPage'));
const SkylarGreyPage = lazy(() => import('@/pages/SkylarGreyPage'));

const RemovedCompanyPageRedirect: React.FC = () => {
  const [, setLocation] = useLocation();

  useEffect(() => {
    setLocation('/');
  }, [setLocation]);

  return null;
};

/**
 * AppRoutes - Component for application routing
 * 
 * @component
 */
function AppRoutes() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        <Route path="/" component={HomePage} />
        <Route path="/reserve" component={ProductPage} />
        <Route path="/blog" component={BlogFeedPage} />
        <Route path="/blog/:id" component={BlogPostPage} />
        <Route path="/news/:id" component={BlogPostPage} />
        <Route path="/waitlist" component={WaitlistPage} />
        <Route path="/delivery-terms" component={DeliveryTermsPage} />
        <Route path="/return-policy" component={ReturnPolicyPage} />
        <Route path="/accessibility" component={AccessibilityPage} />
        <Route path="/privacy" component={PrivacyPolicyPage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/terms" component={TermsOfServicePage} />
        <Route path="/terms-of-service" component={TermsOfServicePage} />
        <Route path="/meditations" component={MeditationsPage} />
        <Route path="/test" component={TestPage} />
        <Route path="/family-stories" component={FamilyStoriesPage} />
        <Route path="/phone-backgrounds" component={PhoneBackgroundsPage} />
        <Route path="/company" component={RemovedCompanyPageRedirect} />
        <Route path="/company/:rest*" component={RemovedCompanyPageRedirect} />
        <Route path="/watch/first-crossing" component={FirstCrossingWatchPage} />
        <Route path="/watch/four-orders" component={FourOrdersPage} />
        <Route path="/plushie" component={MainBranchHomePage} />
        <Route path="/open-app" component={OpenAppRedirect} />
        {/* TEMP_APP_LINK_BRIDGE */}
        <Route path="/app/:rest*" component={AppBridgePage} />
        <Route path="/app" component={AppBridgePage} />
        <Route path="/products/plushie" component={LivingPlushieProductPage} />
        <Route path="/products/merch" component={MerchPage} />
        <Route path="/products/merch/:handle" component={MerchProductPage} />
        <Route path="/reserve/confirmation" component={ReserveConfirmationPage} />
        <Route path="/reserve/confirmed" component={ReserveSubscriptionConfirmedPage} />
        <Route path="/reserve/subscription-confirmed" component={ReserveSubscriptionConfirmedPage} />
        <Route path="/products/living-plushie/instructions" component={LivingPlushieInstructionsPage} />
        <Route path="/skylar-grey" component={SkylarGreyPage} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

/**
 * App - Main application component
 * 
 * @component
 */
function App() {
  return (
    <HelmetProvider>
      <VideoPortalProvider>
        <AppRoutes />
        <GlobalVideoPortal />
        <Toaster />
        <SpeedInsights />
      </VideoPortalProvider>
    </HelmetProvider>
  );
}

export default App;
